import { CountryOfPhone } from 'referenceData';
import { CHINA } from '../referenceData/constants';

const DEFAULT_CORRELATION_ID = '04be0a99-f043-429b-86fd-23cc0d586bc8';
const INVALID_VALUES = ['false', 'n', 'no', 'invalid', 'fail', 'failed', 'error', 'reject', 'rejected'];
const PASSED_RESULT_TYPE = 'PASSED';
const REFER_RESULT_TYPE = 'REFER';
const TAX_RESIDENCY_LIMIT = 4;
const VALIDATION_ARRAY_KEYS = [
  'taxValidations',
  'taxValidationResults',
  'validationResults',
  'validations',
  'results',
  'items',
  'taxResidencyResults',
  'data'
];
const COUNTRY_CODE_OVERRIDES = {
  [CHINA]: 'CN',
  Antarctica: 'AQ',
  'Bolivia, Plurinational State of': 'BO',
  'British Indian Ocean Territory': 'IO',
  'Cabo Verde': 'CV',
  'Christmas Island': 'CX',
  'Congo, Democratic Republic of the': 'CD',
  "Cote D'Ivoire": 'CI',
  'East Timor': 'TL',
  Eswatini: 'SZ',
  'Falkland Islands (Malvinas)': 'FK',
  'Faroe Islands': 'FO',
  'Heard and Mcdonald Islands': 'HM',
  'Holy See (Vatican City State)': 'VA',
  Ireland: 'IE',
  Kiribati: 'KI',
  'Korea, Dem Peoples Rep Of (North)': 'KP',
  Libya: 'LY',
  'Mainland China': 'CN',
  'Hong Kong SAR': 'HK',
  'Macau SAR': 'MO',
  'Norfolk Island': 'NF',
  'Palestine, State of': 'PS',
  Pitcairn: 'PN',
  'Russian Federation': 'RU',
  'Saint Helena, Ascension and Tristan da Cunha': 'SH',
  'Saint Kitts and Nevis': 'KN',
  'Saint Martin (French part)': 'MF',
  'Saint Vincent and the Grenadines': 'VC',
  'Sao Tome and Principe': 'ST',
  'Sint Maarten (Dutch part)': 'SX',
  'South Georgia and South Sandwich Islands': 'GS',
  'Trinidad and Tobago': 'TT',
  'Turks and Caicos Islands': 'TC',
  Ukraine: 'UA',
  'US Virgin Islands': 'VI',
  USA: 'US',
  UK: 'GB',
  'Venezuela, Bolivarian Republic of': 'VE',
  'Viet Nam': 'VN'
};
const NO_TIN_REASON_TYPE_MAP = {
  EXPLANATION1: 'REASON_B_UNABLE_TO_OBTAIN',
  EXPLANATION2: 'REASON_B_UNABLE_TO_OBTAIN',
  REASON4: 'REASON_A_COUNTRY_DOESNOT_ISSUE_TIN',
  REASON6: 'REASON_C_NO_TIN_REQUIRED'
};

const COUNTRY_NAME_TO_CODE = Object.entries(CountryOfPhone.en || {}).reduce((result, [code, name]) => ({
  ...result,
  [name.toLowerCase()]: code
}), {});

const normalizeText = value => String(value || '').trim();

export const getCountryCode = (country) => {
  const countryText = normalizeText(country);

  if (!countryText) {
    return '';
  }

  if (/^[A-Za-z]{2}$/.test(countryText)) {
    return countryText.toUpperCase();
  }

  return COUNTRY_CODE_OVERRIDES[countryText] || COUNTRY_NAME_TO_CODE[countryText.toLowerCase()] || countryText;
};

export const getNoTinReasonType = (reasonForNoTIN) => {
  const reasonText = normalizeText(reasonForNoTIN);
  return NO_TIN_REASON_TYPE_MAP[reasonText] || reasonText;
};

const shouldValidateTin = (item = {}) => !item.agree || item.countryOfTaxResidency === 'China';
const shouldValidateNoTinReason = (item = {}) => (
  item.agree &&
  item.countryOfTaxResidency !== 'China'
);

const getTaxResidenciesForValidation = (financialValues = {}) => (
  (financialValues.additionalNationalities || [])
    .slice(0, TAX_RESIDENCY_LIMIT)
    .filter((item) => shouldValidateTin(item) || shouldValidateNoTinReason(item))
    .map((item) => ({
      countryCode: getCountryCode(item.countryOfTaxResidency),
      ...(shouldValidateTin(item)
        ? { taxIdentificationNumber: item.taxDocumentIdNumber || '' }
        : { noTaxIdentificationNumberReasonType: getNoTinReasonType(item.reasonForNoTIN) })
    }))
);

const getTaxResidencyValidationIndexMap = (financialValues = {}) => (
  (financialValues.additionalNationalities || [])
    .slice(0, TAX_RESIDENCY_LIMIT)
    .reduce((indexes, item, index) => (
      shouldValidateTin(item) || shouldValidateNoTinReason(item)
        ? [...indexes, index]
        : indexes
    ), [])
);

const generateCorrelationId = () => {
  if (typeof crypto !== 'undefined' && crypto.getRandomValues) {
    const bytes = new Uint8Array(16);
    crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    const hex = Array.from(bytes).map((byte) => byte.toString(16).padStart(2, '0'));
    return `${hex.slice(0, 4).join('')}-${hex.slice(4, 6).join('')}-${hex.slice(6, 8).join('')}-${hex.slice(8, 10).join('')}-${hex.slice(10, 16).join('')}`;
  }

  return DEFAULT_CORRELATION_ID;
};

export const buildGlobalCheckTaxValidationRequestBody = (financialValues = {}) => ({
  data: {
    validateTaxRequestDetails: {
      financialDetails: {
        taxPayerIdentification: {
          residencies: getTaxResidenciesForValidation(financialValues)
        }
      }
    }
  }
});

const getValidationItems = (response) => {
  if (!response || typeof response !== 'object') {
    return [];
  }

  if (Array.isArray(response)) {
    return response;
  }

  const items = [];

  for (const key of VALIDATION_ARRAY_KEYS) {
    if (Array.isArray(response[key])) {
      items.push(...response[key]);
    }
  }

  if (response.validateTaxResponseDetails && typeof response.validateTaxResponseDetails === 'object') {
    items.push(...getValidationItems(response.validateTaxResponseDetails));
  }

  if (response.vnrResponse && typeof response.vnrResponse === 'object') {
    items.push(...getValidationItems(response.vnrResponse));
  }

  if (response.data && typeof response.data === 'object') {
    items.push(...getValidationItems(response.data));
  }

  if (response.result && typeof response.result === 'object') {
    items.push(...getValidationItems(response.result));
  }

  if (items.length) {
    return items;
  }

  return [response];
};

const isInvalidValue = (value) => {
  if (value === false) {
    return true;
  }

  if (typeof value === 'string') {
    return INVALID_VALUES.includes(value.toLowerCase());
  }

  return false;
};

const hasInvalidField = (item = {}, fieldNames = []) => {
  const fieldText = [item.field, item.fieldName, item.name, item.type, item.validationType, item.errorCode, item.code, item.message, item.description]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();

  const isTargetField = fieldNames.some((fieldName) => fieldText.includes(fieldName.toLowerCase()));
  const hasInvalidStatus = [item.valid, item.isValid, item.validationPassed, item.passed, item.status, item.result]
    .some(isInvalidValue);

  return isTargetField && (hasInvalidStatus || Boolean(item.code || item.errorCode));
};

const getItemIndex = (item = {}) => {
  if (typeof item.__taxResidencyIndex === 'number') {
    return item.__taxResidencyIndex;
  }

  const rawIndex = item.index ?? item.itemIndex ?? item.sequenceNumber ?? item.seqNo ?? item.sequenceNo;
  const numberIndex = Number(rawIndex);

  if (Number.isNaN(numberIndex)) {
    return null;
  }

  return numberIndex > 0 ? numberIndex - 1 : numberIndex;
};

const getMappedItemIndex = (item = {}, validationIndexMap = []) => {
  const itemIndex = getItemIndex(item);

  if (itemIndex === null) {
    return null;
  }

  return validationIndexMap[itemIndex] ?? itemIndex;
};

const getRelatedItems = (items = [], index, validationIndexMap = []) => items.filter((item) => {
  const itemIndex = getMappedItemIndex(item, validationIndexMap);
  return itemIndex === null || itemIndex === index;
});

const getRelatedTaxResidencyItems = (items = [], residency = {}, index, validationIndexMap = []) => {
  const countryCode = getCountryCode(residency.countryOfTaxResidency);
  const taxIdentificationNumber = normalizeText(residency.taxDocumentIdNumber);
  const noTaxIdentificationNumberReasonType = getNoTinReasonType(residency.reasonForNoTIN);

  const countryMatchedItems = items.filter((item) => {
    const itemCountryCode = getCountryCode(item.countryCode || item.countryOfTaxResidency);
    return itemCountryCode && itemCountryCode === countryCode;
  });

  if (countryMatchedItems.length) {
    return countryMatchedItems;
  }

  const hasCountryIdentifiedItems = items.some((item) => getCountryCode(item.countryCode || item.countryOfTaxResidency));

  const matchedItems = items.filter((item) => {
    const itemCountryCode = getCountryCode(item.countryCode || item.countryOfTaxResidency);
    const itemTaxIdentificationNumber = normalizeText(item.taxIdentificationNumber || item.taxDocumentIdNumber);
    const itemNoTinReason = normalizeText(item.noTaxIdentificationNumberReasonType || item.reasonForNoTIN);
    const hasIdentifyingValue = itemCountryCode || itemTaxIdentificationNumber || itemNoTinReason;

    return hasIdentifyingValue &&
      (!itemCountryCode || itemCountryCode === countryCode) &&
      (!taxIdentificationNumber || !itemTaxIdentificationNumber || itemTaxIdentificationNumber === taxIdentificationNumber) &&
      (!noTaxIdentificationNumberReasonType || !itemNoTinReason || itemNoTinReason === noTaxIdentificationNumberReasonType);
  });

  if (matchedItems.length) {
    return matchedItems;
  }

  if (hasCountryIdentifiedItems) {
    return [];
  }

  const indexMatchedItems = items.filter((item) => getMappedItemIndex(item, validationIndexMap) === index);

  if (indexMatchedItems.length) {
    return indexMatchedItems;
  }

  return getRelatedItems(items, index, validationIndexMap);
};

const getNoTinReasonValidationError = (t) => t('ICO.FINANCIAL.TAX_VALIDATION_REASON_ERROR');

const getValidateTaxResponseDetails = (response = {}) => (
  response?.data?.validateTaxResponseDetails ||
  response?.validateTaxResponseDetails ||
  response
);

const getOverallResultType = (response = {}) => normalizeText(getValidateTaxResponseDetails(response)?.overallResultType).toUpperCase();

const getTaxResidencyResults = (response = {}) => {
  const taxResidencyResults = getValidateTaxResponseDetails(response)?.vnrResponse?.taxResidencyResults;

  return Array.isArray(taxResidencyResults)
    ? taxResidencyResults.map((item, index) => ({ ...item, __taxResidencyIndex: index }))
    : taxResidencyResults;
};

const getFallbackTaxValidationErrors = (financialValues = {}, t) => {
  const additionalNationalities = [];
  const index = (financialValues.additionalNationalities || []).findIndex((item) => (
    shouldValidateTin(item) || shouldValidateNoTinReason(item)
  ));

  if (index < 0) {
    return {};
  }

  const item = financialValues.additionalNationalities[index];
  additionalNationalities[index] = shouldValidateTin(item)
    ? { taxDocumentIdNumber: t('ICO.FINANCIAL.TAX_VALIDATION_TIN_ERROR') }
    : { reasonForNoTIN: getNoTinReasonValidationError(t) };

  return { additionalNationalities };
};

export const getTaxValidationErrors = (financialValues = {}, response = {}, t) => {
  const overallResultType = getOverallResultType(response);

  if (overallResultType === PASSED_RESULT_TYPE) {
    return {};
  }

  const taxResidencyResults = getTaxResidencyResults(response);
  const items = overallResultType === REFER_RESULT_TYPE
    ? (Array.isArray(taxResidencyResults) ? taxResidencyResults : [])
    : getValidationItems(response);
  const additionalNationalities = [];
  const validationIndexMap = getTaxResidencyValidationIndexMap(financialValues);

  (financialValues.additionalNationalities || []).forEach((item, index) => {
    const relatedItems = getRelatedTaxResidencyItems(items, item, index, validationIndexMap);
    const fieldErrors = {};

    const tinInvalid = relatedItems.some((result) => (
      isInvalidValue(result.taxDocumentIdNumberValid) ||
      isInvalidValue(result.tinValid) ||
      isInvalidValue(result.taxIdValid) ||
      result.taxValidationCode === 'TIN_NUMBER_NOT_VALID' ||
      hasInvalidField(result, ['taxDocumentIdNumber', 'tin', 'tax identification number'])
    ));

    const reasonInvalid = relatedItems.some((result) => (
      isInvalidValue(result.reasonForNoTINValid) ||
      isInvalidValue(result.noTinReasonValid) ||
      Boolean(result.noTaxIdentificationNumberReasonWarningCode) ||
      result.taxValidationCode === 'NO_TIN_REASON_NOT_VALID' ||
      hasInvalidField(result, ['reasonForNoTIN', 'noTinReason', 'reason'])
    ));

    if (shouldValidateTin(item) && tinInvalid) {
      fieldErrors.taxDocumentIdNumber = t('ICO.FINANCIAL.TAX_VALIDATION_TIN_ERROR');
    }

    if (shouldValidateNoTinReason(item) && reasonInvalid) {
      fieldErrors.reasonForNoTIN = getNoTinReasonValidationError(t);
    }

    if (Object.keys(fieldErrors).length) {
      additionalNationalities[index] = fieldErrors;
    }
  });

  if (additionalNationalities.length) {
    return { additionalNationalities };
  }

  return overallResultType === REFER_RESULT_TYPE
    ? getFallbackTaxValidationErrors(financialValues, t)
    : {};
};

export const hasTaxValidationErrors = (errors = {}) => Boolean(
  errors.additionalNationalities &&
  errors.additionalNationalities.some &&
  errors.additionalNationalities.some(Boolean)
);

export const getTaxValidationServiceError = (financialValues = {}, t) => {
  const additionalNationalities = [];
  const index = (financialValues.additionalNationalities || []).findIndex((item) => (
    shouldValidateTin(item)
  ));

  if (index >= 0) {
    additionalNationalities[index] = {
      taxDocumentIdNumber: t('ICO.FINANCIAL.TAX_VALIDATION_SERVICE_ERROR')
    };
  } else if (financialValues.additionalNationalities?.length) {
    additionalNationalities[0] = {
      reasonForNoTIN: t('ICO.FINANCIAL.TAX_VALIDATION_SERVICE_ERROR')
    };
  }

  return additionalNationalities.length ? { additionalNationalities } : {};
};

export const globalCheckTaxValidations = async (financialValues = {}) => {
  if (process.env.MOCK === 'on' || !process.env.globalCheckTaxValidationsApi) {
    return { valid: true };
  }

  const requestBody = buildGlobalCheckTaxValidationRequestBody(financialValues);

  if (!requestBody.data.validateTaxRequestDetails.financialDetails.taxPayerIdentification.residencies.length) {
    return { valid: true };
  }

  const response = await fetch(process.env.globalCheckTaxValidationsApi, {
    method: 'POST',
    mode: 'cors',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'X-HSBC-Chnl-CountryCode': 'HK',
      'X-HSBC-Chnl-Group-Member': 'HBAP',
      'X-HSBC-Client-Id': 'dummy',
      'X-HSBC-Global-Channel-Id': 'WEB',
      'X-HSBC-Request-Correlation-Id': generateCorrelationId(),
      'X-HSBC-Source-System-Id': '12648576',
      'X-HSBC-Src-UserAgent': window.navigator.userAgent
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    throw new Error(`Global check tax validations failed with status: ${response.status}`);
  }

  return response.json();
};
